import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { eventLabel } from '@/components/domain/timeline';
import { cx } from '@/components/ui/cx';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/states';
import { isApiError } from '@/lib/api';
import { type ShipmentDetail, shipmentKeys, shipmentsApi } from '@/lib/api/shipments';
import { clockTime, dayMonth, dayMonthTime, relative } from '@/lib/format/time';
import { useNow } from '@/lib/hooks/use-now';
import { referenceOf, useShipment } from '../context';
import { useDeliveries } from '../header-lines';
import {
  applyFilters,
  applyWithdrawnFilters,
  buildRows,
  counts,
  dayKey,
  ESTIMATE_SOURCE,
  exportRows,
  type Filters,
  groupByDay,
  groupWithdrawn,
  KIND_FILTERS,
  type KindFilter,
  placeBook,
  showsTime,
  type TimelineRow,
  toCsv,
  type WithdrawnGroup,
  withdrawnRows,
} from './model';
import './timeline.css';

// S03 · Timeline (07 §3): every event of the shipment, grouped by day, with the
// Plancia estimate; filters (kind, container, source) live in the URL.

export interface TimelineSearch {
  kind?: KindFilter;
  container?: string;
  source?: string;
}

export function validateTimelineSearch(search: Record<string, unknown>): TimelineSearch {
  const out: TimelineSearch = {};
  if (typeof search.kind === 'string' && (KIND_FILTERS as readonly string[]).includes(search.kind))
    out.kind = search.kind as KindFilter;
  if (typeof search.container === 'string' && search.container) out.container = search.container;
  if (typeof search.source === 'string' && search.source) out.source = search.source;
  return out;
}

function useTimelineSearch() {
  const search = useSearch({ strict: false }) as TimelineSearch;
  const navigate = useNavigate();
  const set = (next: TimelineSearch) =>
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => {
        const merged = { ...prev, ...next } as Record<string, unknown>;
        for (const k of Object.keys(merged)) if (merged[k] === undefined) delete merged[k];
        return merged;
      },
      replace: true,
    } as never);
  return { search, set };
}

export function TimelineTab() {
  const shipment = useShipment();
  const { t } = useTranslation('shipment-timeline');
  const query = useQuery({
    queryKey: shipmentKeys.part(shipment.id, 'events'),
    queryFn: () => shipmentsApi.events(shipment.id),
  });
  const deliveries = useDeliveries(shipment.id);

  if (query.isPending)
    return (
      <div className="tl" data-testid="tab-timeline">
        <TimelineSkeleton />
      </div>
    );
  if (query.isError)
    return (
      <div className="tl" data-testid="tab-timeline">
        <ErrorState
          title={t('error.title')}
          body={t('error.body', { status: isApiError(query.error) ? query.error.status : '—' })}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  return (
    <TimelineBody
      shipment={shipment}
      events={query.data.items}
      withdrawn={query.data.withdrawn}
      containers={query.data.containers}
      payloads={deliveries.data?.payloads ?? []}
    />
  );
}

function TimelineBody({
  shipment,
  events,
  withdrawn,
  containers,
  payloads,
}: {
  shipment: ShipmentDetail;
  events: Parameters<typeof buildRows>[1];
  withdrawn: Parameters<typeof buildRows>[1];
  containers: string[];
  payloads: Parameters<typeof buildRows>[2];
}) {
  const { t, i18n } = useTranslation('shipment-timeline');
  const { t: tc } = useTranslation();
  const now = new Date(useNow(60_000));
  const { search, set } = useTimelineSearch();
  const filters: Filters = {
    kind: search.kind ?? 'all',
    container: search.container && containers.includes(search.container) ? search.container : null,
    source: search.source ?? null,
  };
  const rows = useMemo(() => buildRows(shipment, events, payloads), [shipment, events, payloads]);
  const allWithdrawn = useMemo(() => withdrawnRows(withdrawn, placeBook(shipment)), [withdrawn, shipment]);
  const label = (code: string) => t(`code.${code}`, { defaultValue: eventLabel(code, tc, i18n.language) });

  const shown = applyFilters(rows, filters);
  const shownWithdrawn = applyWithdrawnFilters(allWithdrawn, filters);
  const n = counts(rows, allWithdrawn, filters);
  const { days, undated } = groupByDay(shown, now);
  const sources = [...new Set(rows.map((r) => r.source))];
  const multi = containers.length > 1;
  const nothing = rows.length === 0 && allWithdrawn.length === 0;
  const filtered = !nothing && shown.length === 0 && shownWithdrawn.length === 0;
  const clear = () => set({ kind: undefined, container: undefined, source: undefined });

  const download = (kind: 'csv' | 'json') => {
    const data = exportRows(shown, shownWithdrawn, label);
    const body = kind === 'csv' ? toCsv(data) : `${JSON.stringify(data, null, 2)}\n`;
    const blob = new Blob([body], { type: kind === 'csv' ? 'text/csv;charset=utf-8' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${referenceOf(shipment)}-${t('export.file')}.${kind}`;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="tl" data-testid="tab-timeline">
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        {/* biome-ignore lint/a11y/useSemanticElements: a set of toggle buttons (the boards' segmented control), not a form fieldset */}
        <div className="seg" role="group" aria-label={t('filters.kind')}>
          {KIND_FILTERS.map((k) => (
            <button
              key={k}
              type="button"
              className={cx(filters.kind === k && 'on')}
              aria-pressed={filters.kind === k}
              onClick={() => set({ kind: k === 'all' ? undefined : k })}
            >
              {t(`kind.${k}`)} <span className="sub">{n[k]}</span>
            </button>
          ))}
        </div>
        {multi ? (
          // biome-ignore lint/a11y/useSemanticElements: a set of toggle buttons (the boards' segmented control), not a form fieldset
          <div className="seg" role="group" aria-label={t('filters.container')}>
            <button
              type="button"
              className={cx(!filters.container && 'on')}
              aria-pressed={!filters.container}
              onClick={() => set({ container: undefined })}
            >
              {t('filters.allContainers')}
            </button>
            {containers.map((c) => (
              <button
                key={c}
                type="button"
                className={cx('mono', filters.container === c && 'on')}
                aria-pressed={filters.container === c}
                onClick={() => set({ container: c })}
              >
                {c}
              </button>
            ))}
          </div>
        ) : null}
        <SourceMenu sources={sources} value={filters.source} onChange={(source) => set({ source })} />
        <div className="flex" style={{ marginLeft: 'auto', gap: 6 }}>
          <button type="button" className="btn sm" onClick={() => download('csv')} disabled={nothing}>
            {t('export.csv')}
          </button>
          <button type="button" className="btn sm" onClick={() => download('json')} disabled={nothing}>
            {t('export.json')}
          </button>
        </div>
      </div>

      <div className="sub tl-legend flex flex-wrap" style={{ gap: 14 }}>
        <span className="inline-flex items-center" style={{ gap: 6 }}>
          <span className="tg f" />
          {t('legend.fact')}
        </span>
        <span className="inline-flex items-center" style={{ gap: 6 }}>
          <span className="tg c" />
          {t('legend.forecast')}
        </span>
        <span className="inline-flex items-center" style={{ gap: 6 }}>
          <span className="tg e" />
          {t('legend.estimate')}
        </span>
        <span className="inline-flex items-center" style={{ gap: 6 }}>
          <span className="tag c-ok b-ok">{t('tag.ais')}</span>
          {t('legend.ais')}
        </span>
        <span>{t('legend.hover')}</span>
      </div>

      {nothing ? (
        <EmptyState
          title={t('empty.title')}
          body={
            shipment.next_check_at
              ? t('empty.bodyNext', { when: relative(new Date(shipment.next_check_at), now, tc) })
              : t('empty.body')
          }
        />
      ) : filtered ? (
        <EmptyState
          title={t('filtered.title')}
          body={t('filtered.body')}
          action={
            <button type="button" className="btn sm" onClick={clear}>
              {tc('actions.clearFilters')}
            </button>
          }
        />
      ) : (
        <section className="card" style={{ overflow: 'hidden' }} aria-label={t('aria')}>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 900 }}>
              <div className="tl-ev head">
                <span>{t('cols.time')}</span>
                <span />
                <span>{t('cols.event')}</span>
                <span>{t('cols.place')}</span>
                <span>{t('cols.vessel')}</span>
                <span>{t('cols.source')}</span>
              </div>
              {days.map((d) => (
                // biome-ignore lint/a11y/useSemanticElements: the rows of one day, named by its header
                <div key={d.key} role="group" aria-label={dayTitle(d.date, i18n.language)}>
                  <div className="tl-day">
                    <span>{dayTitle(d.date, i18n.language)}</span>
                    {d.future ? (
                      <span className="sub" style={{ fontWeight: 400 }}>
                        {t('day.future')}
                      </span>
                    ) : null}
                  </div>
                  {d.rows.map((r) => (
                    <EventRow key={r.id} row={r} multi={multi} label={label(r.code)} now={now} />
                  ))}
                </div>
              ))}
              {undated.length > 0 ? (
                // biome-ignore lint/a11y/useSemanticElements: the rows of one day, named by its header
                <div role="group" aria-label={t('day.undated')}>
                  <div className="tl-day">
                    <span>{t('day.undated')}</span>
                  </div>
                  {undated.map((r) => (
                    <EventRow key={r.id} row={r} multi={multi} label={label(r.code)} now={now} />
                  ))}
                </div>
              ) : null}
              {groupWithdrawn(shownWithdrawn).map((g) => (
                <WithdrawnBlock
                  key={g.reason}
                  group={g}
                  label={label}
                  forceOpen={filters.kind === 'withdrawn'}
                  first={shown.length === 0}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function dayTitle(date: Date, language: string): string {
  const weekday = new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'it-IT', { weekday: 'long' }).format(
    date,
  );
  return `${weekday} ${dayMonth(date)}`;
}

const sameDay = (a: Date, b: Date) => dayKey(a) === dayKey(b);

function EventRow({
  row: r,
  multi,
  label,
  now,
}: {
  row: TimelineRow;
  multi: boolean;
  label: string;
  now: Date;
}) {
  const { t } = useTranslation('shipment-timeline');
  const shipment = useShipment();
  const glyph = r.kind === 'fact' ? 'f' : r.kind === 'forecast' ? 'c' : 'e';
  const at = r.readAt ? (sameDay(r.readAt, now) ? clockTime(r.readAt) : dayMonthTime(r.readAt)) : null;
  return (
    <div
      className="tl-ev"
      data-testid="tl-row"
      data-kind={r.kind}
      title={r.originalText ? t('original', { text: r.originalText }) : undefined}
    >
      <span
        className="mono"
        title={r.at ? `${r.at.toISOString().slice(0, 16).replace('T', ' ')} UTC` : undefined}
      >
        {r.at && showsTime(r) ? clockTime(r.at) : '—'}
      </span>
      <span className={`tg ${glyph}`} role="img" aria-label={t(`legend.${r.kind}`)} />
      <div className="flex flex-col" style={{ gap: 3, minWidth: 0 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
          <span style={{ fontWeight: 500 }}>{label}</span>
          {r.kind === 'forecast' ? <span className="tag">{t('tag.forecast')}</span> : null}
          {r.kind === 'estimate' ? <span className="tag c-es b-es">{t('tag.estimate')}</span> : null}
          {r.ais ? <span className="tag c-ok b-ok">{t('tag.ais')}</span> : null}
          {multi && r.container ? <span className="chip mono tl-cnt">{r.container}</span> : null}
        </div>
        <span className="sub mono">{r.code}</span>
      </div>
      <div>
        <div>{placeLine(r.place)}</div>
        {r.kind === 'estimate' ? (
          <div className="sub">«{t('estimatePlace')}»</div>
        ) : r.originalText ? (
          <div className="sub">«{r.originalText}»</div>
        ) : null}
      </div>
      <div>
        <div>{r.vessel ?? '—'}</div>
        {r.vessel && r.voyage ? <div className="sub mono">{r.voyage}</div> : null}
      </div>
      <div className="flex flex-col" style={{ gap: 3 }}>
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          <span className="chip">{t(`source.${r.source}`, { defaultValue: r.source })}</span>
          {r.kind === 'estimate' ? (
            <Link
              className="sub tl-read"
              to="/shipments/$id/route"
              params={{ id: shipment.id }}
              title={t('basisTitle')}
            >
              {t(`basis.${r.basis ?? 'unknown'}`, { defaultValue: r.basis ?? '' })}
            </Link>
          ) : r.readId ? (
            <Link
              className="sub tl-read"
              to="/shipments/$id/reads"
              params={{ id: shipment.id }}
              search={{ read: r.readId } as never}
            >
              {t('read', { at })}
            </Link>
          ) : r.observedBy === 'dokless' ? (
            <span className="sub tl-read">{t('fromDokless')}</span>
          ) : null}
        </div>
        {r.revision !== null ? <span className="sub">{t('sent', { rev: r.revision })}</span> : null}
      </div>
    </div>
  );
}

/** «SAJED Jeddah»: the code in mono, the name after it. */
function placeLine(place: string | null) {
  if (!place) return '—';
  const m = /^([A-Z]{2}[A-Z0-9]{3})\s+(.*)$/.exec(place);
  if (!m) return place;
  return (
    <>
      <span className="mono">{m[1]}</span> {m[2]}
    </>
  );
}

function WithdrawnBlock({
  group: g,
  label,
  forceOpen,
  first,
}: {
  group: WithdrawnGroup;
  label: (code: string) => string;
  forceOpen: boolean;
  first: boolean;
}) {
  const { t, i18n } = useTranslation('shipment-timeline');
  const [open, setOpen] = useState(false);
  const expanded = open || forceOpen;
  const month =
    g.month &&
    new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-GB' : 'it-IT', { month: 'long' }).format(g.month);
  const why =
    g.reason === 'previous_life' && g.from && g.to && month
      ? t('withdrawn.voyage', { from: g.from, to: g.to, month })
      : t(`withdrawn.reason.${g.reason}`, { defaultValue: g.reason });
  const id = `tl-wd-${g.reason}`;
  return (
    <div className="tl-wd" style={first ? { borderTop: 0 } : undefined} data-testid="tl-withdrawn">
      <button
        type="button"
        className="btn sm gh"
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        <Icon as={ChevronRight} size="s" />
        <strong>{t('withdrawn.count', { count: g.rows.length })}</strong>
        <span className="m2">· {why}</span>
      </button>
      <div className="m2" style={{ paddingLeft: 22 }}>
        {t(`withdrawn.rule.${g.reason}`, { defaultValue: t('withdrawn.rule.manual') })}
      </div>
      {expanded ? (
        <div id={id} className="flex flex-col" style={{ paddingLeft: 22 }}>
          {g.rows.map((r) => (
            <div key={r.id} className="tl-wd-row">
              <span className="mono">{r.at ? (r.dateOnly ? dayMonth(r.at) : dayMonthTime(r.at)) : '—'}</span>
              <span className="tg w" role="img" aria-label={t('legend.withdrawn')} />
              <span style={{ textDecoration: 'line-through' }}>{label(r.code)}</span>
              <span>{r.place ? placeLineName(r.place) : '—'}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const placeLineName = (place: string) => place.replace(/^[A-Z]{2}[A-Z0-9]{3}\s+/, '');

function SourceMenu({
  sources,
  value,
  onChange,
}: {
  sources: string[];
  value: string | null;
  onChange(source: string | undefined): void;
}) {
  const { t } = useTranslation('shipment-timeline');
  const name = (s: string) => t(`source.${s}`, { defaultValue: s });
  const order = ['carrier', ESTIMATE_SOURCE, 'manual'];
  const list = [...sources].sort(
    (a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99) || a.localeCompare(b),
  );
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" className="btn sm">
          {t('filters.source', { source: value ? name(value) : t('filters.allSources') })}
          <Icon as={ChevronDown} size="s" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="pl dlg tl-menu"
          style={{ minWidth: 180, padding: 6, zIndex: 'var(--z-dialog)', background: 'var(--color-surface)' }}
        >
          <DropdownMenu.RadioGroup
            value={value ?? ''}
            onValueChange={(v) => onChange(v === '' ? undefined : v)}
          >
            {['', ...list].map((s) => (
              <DropdownMenu.RadioItem key={s || 'all'} value={s}>
                <span style={{ width: 13, display: 'inline-flex' }}>
                  <DropdownMenu.ItemIndicator>
                    <Icon as={Check} size="s" />
                  </DropdownMenu.ItemIndicator>
                </span>
                {s ? name(s) : t('filters.allSources')}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function TimelineSkeleton() {
  const { t } = useTranslation('shipment-timeline');
  return (
    <>
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <Skeleton width={270} height={32} />
        <Skeleton width={100} height={26} />
        <span style={{ marginLeft: 'auto' }}>
          <Skeleton width={180} height={26} />
        </span>
      </div>
      <Skeleton width={620} height={12} />
      <section
        className="card"
        style={{ overflow: 'hidden' }}
        role="status"
        aria-busy="true"
        aria-label={t('loading')}
      >
        <div className="tl-ev head">
          <span>{t('cols.time')}</span>
          <span />
          <span>{t('cols.event')}</span>
          <span>{t('cols.place')}</span>
          <span>{t('cols.vessel')}</span>
          <span>{t('cols.source')}</span>
        </div>
        {[0, 1, 2].map((d) => (
          <div key={d}>
            <div className="tl-day">
              <Skeleton width={110} height={12} />
            </div>
            {[0, 1].map((r) => (
              <div className="tl-ev" key={r}>
                <Skeleton width={40} height={12} />
                <Skeleton width={11} height={11} round />
                <span className="flex flex-col" style={{ gap: 6 }}>
                  <Skeleton width="60%" height={12} />
                  <Skeleton width="40%" height={10} />
                </span>
                <span className="flex flex-col" style={{ gap: 6 }}>
                  <Skeleton width="70%" height={12} />
                  <Skeleton width="50%" height={10} />
                </span>
                <Skeleton width="60%" height={12} />
                <Skeleton width="70%" height={12} />
              </div>
            ))}
          </div>
        ))}
      </section>
    </>
  );
}
